import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import api from "../utils/api";
import LogoutButton from "../components/LogoutButton";
import Sidebar from "../components/Sidebar";
import "../style/adminreport.css";

function AdminReports() {
  const navigate = useNavigate();
  const [reportsData, setReportsData] = useState([]);
  const [search, setSearch] = useState("");
  const [filterWing, setFilterWing] = useState("");
  const [filterFlat, setFilterFlat] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [modalImage, setModalImage] = useState(null);
  const rowsPerPage = 10;

  useEffect(() => {
    setLoading(true);
    api.get("/meter-readings/all-page")
      .then(response => {
        console.log("API Response:", response.data);
        const formattedData = response.data.map(item => ({
          id: item.id,
          wing: item.wingName || item.wing || "N/A",
          floor: (item.floorNo !== undefined && item.floorNo !== null) ? String(item.floorNo) : (item.floorName || item.floor || "N/A"),
          flatNo: item.flatNo || "N/A",
          prevReading: item.previousReading || 0,
          currReading: item.currentReading || 0,
          difference: item.difference || 0,
          // image: item.fileData ? `data:image/jpeg;base64,${item.fileData}` : null,
          date: item.date ? new Date(item.date).toISOString().split('T')[0] : "N/A",
        }));
        console.log("Formatted Data:", formattedData);
        setReportsData(formattedData);
      })
      .catch(error => console.error("Error fetching data:", error))
      .finally(() => setLoading(false));
  }, []);


  function getImageById(id) {
    setModalImage(null);
    api.get("/meter-readings/getImageById/" + id)
      .then(response => {

        // console.log(response.data.image);
        setModalImage(response.data.image);
      })
      .catch(error => console.error("Error fetching data:", error))
      .finally(() => setLoading(false));
  }
  const formatDate = (date) => {
    if (!date || date === "N/A") return "N/A";
    const parsed = new Date(date);
    if (isNaN(parsed.getTime())) return "N/A";
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return parsed.toLocaleDateString('en-GB', options).replace(',', '');
  };

  const filteredData = reportsData
    .filter((report) => report.wing.includes(filterWing))
    .filter((report) => report.flatNo.includes(filterFlat))
    .filter((report) =>
      (!startDate || (report.date !== "N/A" && report.date >= startDate)) &&
      (!endDate || (report.date !== "N/A" && report.date <= endDate))
    )
    .filter((report) =>
      report.wing.toLowerCase().includes(search.toLowerCase()) ||
      (report.floor && report.floor.toLowerCase().includes(search.toLowerCase())) ||
      report.flatNo.includes(search)
    );

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const paginatedData = filteredData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const exportFormattedReport = () => {
    // Group readings by flat (wing + floor + flatNo) and by month
    const flatsMap = {};
    const monthsSet = new Set();

    filteredData.forEach(report => {
      const wing = report.wing || "N/A";
      const floor = report.floor || "N/A";
      const flatNo = report.flatNo || "N/A";
      const key = `${wing}___${floor}___${flatNo}`;

      if (!flatsMap[key]) {
        flatsMap[key] = {
          wing,
          floor,
          flatNo,
          readings: {}
        };
      }

      if (report.date && report.date !== "N/A") {
        const date = new Date(report.date);
        if (!isNaN(date.getTime())) {
          const monthKey = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
          monthsSet.add(monthKey);
          flatsMap[key].readings[monthKey] = {
            reading: report.currReading,
            date: formatDate(report.date)
          };
        }
      }
    });

    const sortedMonths = Array.from(monthsSet).sort((a, b) => new Date(a) - new Date(b));

    // Sort flats by Wing, then Floor, then Flat No
    const flatsList = Object.values(flatsMap).sort((a, b) => {
      const wingCompare = (a.wing || "").localeCompare(b.wing || "", undefined, { numeric: true, sensitivity: 'base' });
      if (wingCompare !== 0) return wingCompare;
      const floorCompare = (a.floor || "").localeCompare(b.floor || "", undefined, { numeric: true, sensitivity: 'base' });
      if (floorCompare !== 0) return floorCompare;
      return (a.flatNo || "").localeCompare(b.flatNo || "", undefined, { numeric: true, sensitivity: 'base' });
    });

    // Build worksheet data with merged headers
    const wsData = [];

    // Row 1: Merged month headers
    const headerRow1 = ["Wing Name", "Floor Name", "Flat No."];
    sortedMonths.forEach(month => {
      headerRow1.push(month, "");
    });
    wsData.push(headerRow1);

    // Row 2: Sub-headers
    const headerRow2 = ["Wing Name", "Floor Name", "Flat No."];
    sortedMonths.forEach(() => {
      headerRow2.push("Meter reading", "Date of reading");
    });
    wsData.push(headerRow2);

    // Data rows
    flatsList.forEach(flat => {
      const row = [flat.wing, flat.floor, flat.flatNo];
      sortedMonths.forEach(month => {
        const data = flat.readings[month];
        row.push(data && data.reading !== undefined && data.reading !== null ? parseFloat(Number(data.reading).toFixed(2)) : "", data ? data.date : "");
      });
      wsData.push(row);
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Merge cells for Wing Name, Floor Name, Flat No (row 0 to row 1) and month headers (row 0)
    const merges = [
      { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } },
      { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } },
      { s: { r: 0, c: 2 }, e: { r: 1, c: 2 } }
    ];

    sortedMonths.forEach((_, index) => {
      const startCol = 3 + (index * 2);
      const endCol = startCol + 1;
      merges.push({ s: { r: 0, c: startCol }, e: { r: 0, c: endCol } });
    });
    ws['!merges'] = merges;

    // Set column widths
    ws['!cols'] = [
      { wch: 15 },
      { wch: 15 },
      { wch: 12 },
      ...sortedMonths.flatMap(() => [{ wch: 16 }, { wch: 18 }])
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, ws, "Formatted Report");
    XLSX.writeFile(workbook, "formatted_report.xlsx");
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData.map(report => ({
      Building: report.wing,
      Floor: report.floor,
      "Flat No": report.flatNo,
      // "Previous Reading": report.prevReading.toFixed(2),
      "Current Reading": report.currReading.toFixed(2),
      // Difference: (report.currReading - report.prevReading).toFixed(2),
      Date: formatDate(report.date),
    })));

    worksheet["!protect"] = {
      password: "admin@123",
      selectLockedCells: true,
      selectUnlockedCells: true,
      formatCells: false,
      formatColumns: false,
      formatRows: false,
      insertColumns: false,
      insertRows: false,
      insertHyperlinks: false,
      deleteColumns: false,
      deleteRows: false,
      sort: false,
      autoFilter: false,
      pivotTables: false,
      objects: false,
      scenarios: false,
    };

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reports");

    // Write the file with sheet protection
    XLSX.writeFile(workbook, "reports.xlsx");
  };

  return (
    <div className="admin-layout">
      <Sidebar />
      <div className="admin-workspace">
        <div className="reports-wrapper" style={{ minHeight: "100vh", width: "100%", position: "relative" }}>
          <nav className="reports-navbar" style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 20px", width: "100%" }}>
            <span className="reports-title">Water Billing Reports</span>
            <div style={{ display: 'none' }}><LogoutButton /></div>
          </nav>
      {/* <div className="reports-search-container">
        <input
          type="text"
          placeholder="Search by Wing, Flat No, or Date..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="reports-search"
        />
      </div> */}

      <div className="reports-filter-container">
        <input
          type="text"
          placeholder="Search by Wing, Flat No, or Date..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="reports-search"
        />
        <input type="text" placeholder="Filter by Wing..." value={filterWing} onChange={(e) => setFilterWing(e.target.value)} className="reports-filter" />
        <input type="text" placeholder="Filter by Flat No..." value={filterFlat} onChange={(e) => setFilterFlat(e.target.value)} className="reports-filter" />
      </div>

      <div className="reports-filter-containerr">
        <div
          className="date-filter-box"
          onClick={(e) => {
            const input = e.currentTarget.querySelector("input[type='date']");
            if (input && e.target !== input && !e.target.classList.contains("clear-date-btn")) {
              input.focus();
              try {
                if (input.showPicker) input.showPicker();
              } catch (err) {}
            }
          }}
        >
          <span className="date-label">Start Date:</span>
          <input
            type="date"
            value={startDate}
            max={endDate || undefined}
            onChange={(e) => setStartDate(e.target.value)}
            onClick={(e) => {
              try {
                if (e.target.showPicker) e.target.showPicker();
              } catch (err) {}
            }}
            className="date-input"
            aria-label="Start Date"
          />
          {startDate && (
            <button
              type="button"
              className="clear-date-btn"
              onClick={(e) => {
                e.stopPropagation();
                setStartDate("");
              }}
              title="Clear Start Date"
            >
              &times;
            </button>
          )}
        </div>

        <div
          className="date-filter-box"
          onClick={(e) => {
            const input = e.currentTarget.querySelector("input[type='date']");
            if (input && e.target !== input && !e.target.classList.contains("clear-date-btn")) {
              input.focus();
              try {
                if (input.showPicker) input.showPicker();
              } catch (err) {}
            }
          }}
        >
          <span className="date-label">End Date:</span>
          <input
            type="date"
            value={endDate}
            min={startDate || undefined}
            onChange={(e) => setEndDate(e.target.value)}
            onClick={(e) => {
              try {
                if (e.target.showPicker) e.target.showPicker();
              } catch (err) {}
            }}
            className="date-input"
            aria-label="End Date"
          />
          {endDate && (
            <button
              type="button"
              className="clear-date-btn"
              onClick={(e) => {
                e.stopPropagation();
                setEndDate("");
              }}
              title="Clear End Date"
            >
              &times;
            </button>
          )}
        </div>

        <button className="export-btn" onClick={exportToExcel}>
          <i className="fas fa-file-excel"></i> Export to Excel
        </button>
        <button className="export-btn" onClick={exportFormattedReport}>
          <i className="fas fa-file-alt"></i> Formatted Report
        </button>
      </div>

      <div className="reports-table-wrapper">
        <table className="reports-table">
          <thead>
            <tr>
              <th>Building</th>
              <th>Flat No</th>
              {/* <th>Previous Reading</th> */}
              <th>Current Reading</th>
              {/* <th>Difference</th> */}
              <th>Uploaded Image</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="table-loader">Loading...</td></tr>
            ) : paginatedData.length === 0 ? (
              <tr><td colSpan="7">No reports available</td></tr>
            ) : (
              paginatedData.map(report => (
                <tr key={report.id}>
                  <td>{report.wing}</td>
                  <td>{report.flatNo}</td>
                  {/* <td>{report.prevReading.toFixed(2)}</td> */}
                  <td>{report.currReading.toFixed(2)}</td>
                  {/* <td>{report.difference.toFixed(2)}</td> */}
                  <td>
                    {<button onClick={() => { getImageById(report.id) }}>View Image</button>}
                  </td>
                  <td>{formatDate(report.date)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modalImage && (
        <div className="modal" onClick={() => setModalImage(null)}>
          <img src={`${modalImage}`} alt="Meter" className="modal-content" />
        </div>
      )}
      <div className="pagination">
        <button disabled={
          currentPage === 1
        }
          onClick={() => setCurrentPage(currentPage - 1)}>prev</button>

        <span>page{currentPage} of {totalPages}</span>
        <button disabled={
          currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}>Next</button>
      </div>
      <div className="footer">
        <p>powered by @Dexpert Systems</p>
      </div>
    </div>
      </div>
    </div>
  );
}

export default AdminReports;